// Saved searches (Phase 3) - the catalog "Salvează căutarea" button.
//
// Captures the CURRENT catalog filter state (the same canonical param set the
// server's atpl_build_catalog_filters() accepts) and POSTs it to admin-ajax.
// Logged-out users are prompted to log in / register (reusing the site auth
// flow). Success shows a small inline confirmation with an optional custom name
// and a link to the account's "Căutări salvate" tab.
import { qs, on } from '../lib/dom.js';
import { getFilterState } from './filter-panel.js';
import { t } from '../lib/i18n.js';

const AP = (typeof window !== 'undefined' && window.AP_DATA) ? window.AP_DATA : {};

// Map the client filter STATE → the canonical GET-style params the catalog +
// REST + saved-search matcher all speak (brand/model/body/... comma lists +
// numeric ranges + mode). Sort/view/page are display concerns, not part of a
// saved search.
function collectFilters() {
  const s = getFilterState();
  const out = {};
  const list = (key, arr) => { if (arr && arr.length) out[key] = arr.join(','); };
  const num = (key, v) => { if (v != null && v !== '' && Number(v) > 0) out[key] = Number(v); };

  list('brand', s.brands);
  list('model', s.models);
  list('body', s.bodies);
  list('fuel', s.fuels);
  list('trans', s.trans);
  list('drive', s.drives);
  list('color', s.colors);
  list('collection', s.collection);
  list('options', s.options);
  list('engine', s.engine);
  list('seats', s.seats);
  list('origin', s.origin);
  list('city', s.cities);
  if (s.mode && s.mode !== 'all') out.mode = s.mode;
  num('priceMin', s.priceMin);
  num('priceMax', s.priceMax);
  num('rateMin', s.rateMin);
  num('rateMax', s.rateMax);
  num('powerMin', s.powerMin);
  num('powerMax', s.powerMax);
  num('yearMin', s.yearMin);
  num('yearMax', s.yearMax);
  num('kmMin', s.kmMin);
  num('kmMax', s.kmMax);
  return out;
}

// A rough label seed from the visible applied-filter chips (the server generates
// the authoritative label if this is left blank).
function seedLabel() {
  const chips = qs('[data-role="applied-list"]');
  if (!chips) return '';
  const parts = Array.from(chips.querySelectorAll('.ap-applied__chip'))
    .map((c) => c.textContent.replace(/×\s*$/, '').trim())
    .filter(Boolean);
  return parts.slice(0, 4).join(' · ');
}

function closePanel(container) {
  const panel = qs('.ap-savesearch__panel', container);
  if (panel) panel.remove();
  container.classList.remove('is-open');
}

// On small screens the CSS renders the panel as a viewport-anchored fixed sheet
// (left/right insets keep it on-screen horizontally regardless of where the
// trigger sits in the toolbar). We only need to pin it vertically just below
// the trigger; desktop keeps the CSS absolute dropdown untouched.
function positionMobilePanel(container, panel) {
  if (!window.matchMedia('(max-width: 768px)').matches) {
    panel.style.top = '';
    return;
  }
  const trigger = qs('[data-action="save-search"]', container);
  const rect = (trigger || container).getBoundingClientRect();
  panel.style.top = Math.round(rect.bottom + 8) + 'px';
}

function buildGuestPanel() {
  const wrap = document.createElement('div');
  wrap.className = 'ap-savesearch__panel';
  const authUrl = AP.authUrl || (AP.home ? AP.home + 'auth/' : '/auth/');
  const back = encodeURIComponent(window.location.href);
  wrap.innerHTML = `
    <p class="ap-savesearch__msg">${t('ss_guest_msg', 'Autentifică-te ca să salvezi această căutare și să primești alerte când apar mașini noi.')}</p>
    <a class="ap-btn ap-btn--primary ap-btn--sm ap-btn--block" href="${authUrl}${authUrl.indexOf('?') > -1 ? '&' : '?'}redirect_to=${back}">${t('ss_guest_cta', 'Autentificare / Înregistrare')}</a>
  `;
  return wrap;
}

function buildFormPanel() {
  const wrap = document.createElement('div');
  wrap.className = 'ap-savesearch__panel';
  wrap.innerHTML = `
    <label class="ap-savesearch__field">
      <span class="ap-savesearch__field-label">${t('ss_name_label', 'Numele căutării')}</span>
      <input type="text" class="ap-input ap-savesearch__input" maxlength="180" placeholder="${t('ss_name_label', 'Numele căutării')}">
    </label>
    <div class="ap-savesearch__panel-actions">
      <button type="button" class="ap-btn ap-btn--primary ap-btn--sm" data-role="ss-confirm">${t('ss_save', 'Salvează')}</button>
    </div>
    <p class="ap-savesearch__status" data-role="ss-status" hidden></p>
  `;
  const input = qs('.ap-savesearch__input', wrap);
  if (input) input.value = seedLabel();
  return wrap;
}

async function submitSave(container, panel) {
  const btn = qs('[data-role="ss-confirm"]', panel);
  const input = qs('.ap-savesearch__input', panel);
  const status = qs('[data-role="ss-status"]', panel);
  const label = input ? input.value.trim() : '';

  if (btn) { btn.disabled = true; btn.textContent = t('ss_saving', 'Se salvează…'); }

  const body = new URLSearchParams();
  body.set('action', 'atpl_save_search');
  body.set('nonce', AP.savedSearchNonce || '');
  body.set('filters', JSON.stringify(collectFilters()));
  body.set('url', window.location.href);
  body.set('lang', AP.lang || 'ro');
  if (label) body.set('label', label);

  let data;
  try {
    const res = await fetch(AP.ajaxUrl || '/wp-admin/admin-ajax.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body: body.toString(),
    });
    data = await res.json();
  } catch (err) {
    data = { success: false, data: { message: t('error_retry', 'A apărut o eroare. Încearcă din nou.') } };
  }

  if (data && data.success) {
    const d = data.data || {};
    const account = d.accountUrl || AP.account || '/cont/';
    panel.innerHTML = `
      <div class="ap-savesearch__done">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
        <span>${d.message || t('ss_saved', 'Căutare salvată.')}</span>
      </div>
      <a class="ap-btn ap-btn--outline ap-btn--sm ap-btn--block" href="${account}">${t('ss_view_saved', 'Vezi căutările salvate')}</a>
    `;
    const btnEl = qs('[data-action="save-search"]', container);
    if (btnEl) btnEl.classList.add('is-saved');
    setTimeout(() => closePanel(container), 6000);
  } else {
    const msg = (data && data.data && data.data.message) ? data.data.message : t('ss_save_failed', 'Nu am putut salva căutarea.');
    if (data && data.data && data.data.authUrl) {
      // Session lapsed to guest - swap to the login prompt.
      const guest = buildGuestPanel();
      panel.replaceWith(guest);
      positionMobilePanel(container, guest);
      return;
    }
    if (status) { status.hidden = false; status.textContent = msg; }
    if (btn) { btn.disabled = false; btn.textContent = t('ss_save', 'Salvează'); }
  }
}

export function initSavedSearch() {
  const container = qs('[data-component="save-search"]');
  if (!container) return;
  const trigger = qs('[data-action="save-search"]', container);
  if (!trigger) return;

  const loggedIn = !!(AP.userId && Number(AP.userId) > 0);

  on(trigger, 'click', () => {
    if (container.classList.contains('is-open')) { closePanel(container); return; }
    const panel = loggedIn ? buildFormPanel() : buildGuestPanel();
    container.appendChild(panel);
    container.classList.add('is-open');
    positionMobilePanel(container, panel);

    if (loggedIn) {
      const confirmBtn = qs('[data-role="ss-confirm"]', panel);
      const input = qs('.ap-savesearch__input', panel);
      if (confirmBtn) on(confirmBtn, 'click', () => submitSave(container, panel));
      if (input) {
        on(input, 'keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); submitSave(container, panel); } });
        input.focus();
        input.select();
      }
    }
  });

  // Close on outside click / Escape.
  on(document, 'click', (e) => {
    if (container.classList.contains('is-open') && !container.contains(e.target)) closePanel(container);
  });
  on(document, 'keydown', (e) => { if (e.key === 'Escape') closePanel(container); });
}

// Lightweight confirm guard for account forms tagged with data-confirm (e.g. the
// saved-search delete). Always-on; no-op when no such forms exist.
export function initConfirmForms() {
  document.addEventListener('submit', (e) => {
    const form = e.target.closest ? e.target.closest('form[data-confirm]') : null;
    if (!form) return;
    const msg = form.getAttribute('data-confirm');
    if (msg && !window.confirm(msg)) e.preventDefault();
  });
}
